import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import InframailIpsTable from '../inframail-ips-table';

import type { IProps } from './inframail-ips-content-container';
import { InframailIpAction } from '../../enums';
import { InframailData, InframailPaginationMeta } from '../../types';

import toaster from '../../../../shared/toaster';
import DeleteModal from '../../modals/delete-modal';
import RevokeModal from '../../modals/revoke-modal';

import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../shared/utils';
import { formatInframailIpsData } from '../../utils/format-inframail-ips-data';

const InframailIpsContent: React.FC<IProps> = ({
  inframailIps,
  inframailIpsDomains,
  inframailIpsMeta,
  updateInframailIpsMeta,

  sendGetInframailIpsRequest,
  getInframailIpsRequestStatus,
  getInframailIpsRequestError,
  resetGetInframailIpsRequestState,

  sendDeleteRevokeInframailIpsRequest,
  deleteRevokeInframailIpsRequestStatus,
  deleteRevokeInframailIpsRequestError,
  resetDeleteRevokeInframailIpsRequestState,

  sendGetInframailIpsDomainsRequest,
  getInframailIpsDomainsRequestStatus,
  getInframailIpsDomainsRequestError,
  resetGetInframailIpsDomainsRequestState,

  setEmailAccountInfrastructureDedicatedIpsCount,
  setSelectedDomainsForNewEmailAccounts,
}) => {
  const history = useHistory();
  const actionRowRef = useRef<InframailData>(null);

  const [action, setAction] = useState<InframailIpAction>(null);
  const [actionRow, setActionRow] = useState<InframailData>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);

  const onShowDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setAction(null);
    setActionRow(null);
  };

  const onShowRevokeModal = () => {
    setShowRevokeModal(true);
  };

  const onCloseRevokeModal = () => {
    setShowRevokeModal(false);
    setAction(null);
    setActionRow(null);
  };

  const onAction = (key: string, row: InframailData) => {
    setAction(key as InframailIpAction);
    setActionRow(row);

    if (key === InframailIpAction.DELETE) {
      onShowDeleteModal();
      return;
    }

    if (key === InframailIpAction.REVOKE) {
      onShowRevokeModal();
    }
  };

  const onPaginationChange = (meta: Partial<InframailPaginationMeta>) => {
    const payload = {
      ...inframailIpsMeta,
      ...meta,
    };

    updateInframailIpsMeta(payload);

    const { page, pageSize } = payload;
    sendGetInframailIpsRequest({ page, pageSize });
  };

  const onAddMoreEmailAccountsClicked = (row: InframailData) => {
    actionRowRef.current = row;
    sendGetInframailIpsDomainsRequest(row.id);
  };

  const onAddMoreDomainsClicked = (row: InframailData) => {
    setEmailAccountInfrastructureDedicatedIpsCount(1);
    history.push(
      `/email-accounts/create-email-accounts?setup=InfraMail&step=purchase-domains&hostId=${row.id}`,
    );
  };

  useEffect(() => {
    sendGetInframailIpsRequest({
      page: inframailIpsMeta.page,
      pageSize: inframailIpsMeta.pageSize,
    });
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: getInframailIpsRequestStatus,
      onSuccess: () => {
        resetGetInframailIpsRequestState();
      },
      onFailed: () => {
        resetGetInframailIpsRequestState();
        executeOnErrorWithErrorCheck({
          error: getInframailIpsRequestError,
          onError: () => {
            toaster.error(getInframailIpsRequestError.message);
          },
        });
      },
    });
  }, [getInframailIpsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getInframailIpsDomainsRequestStatus,
      onSuccess: () => {
        resetGetInframailIpsDomainsRequestState();

        if (actionRowRef.current) {
          setSelectedDomainsForNewEmailAccounts(
            formatInframailIpsData(
              {
                id: actionRowRef.current.orderId,
                ipAddress: actionRowRef.current.ipAddress,
              },
              inframailIpsDomains[actionRowRef.current.id] ?? [],
            ),
          );

          history.push('/email-accounts/add-email-accounts');
        }
      },
      onFailed: () => {
        resetGetInframailIpsDomainsRequestState();
        executeOnErrorWithErrorCheck({
          error: getInframailIpsDomainsRequestError,
          onError: () => {
            toaster.error(getInframailIpsDomainsRequestError.message);
          },
        });
      },
    });
  }, [getInframailIpsDomainsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: deleteRevokeInframailIpsRequestStatus,
      onSuccess: () => {
        toaster.success(`Infra Ips ${action.toLowerCase()}d successfully`);
        resetDeleteRevokeInframailIpsRequestState();

        setAction(null);
        setActionRow(null);

        onCloseDeleteModal();
        onCloseRevokeModal();

        sendGetInframailIpsRequest({
          page: 1,
          pageSize: inframailIpsMeta.pageSize,
        });
      },
      onFailed: () => {
        resetDeleteRevokeInframailIpsRequestState();

        setAction(null);
        setActionRow(null);

        onCloseDeleteModal();
        onCloseRevokeModal();

        executeOnErrorWithErrorCheck({
          error: deleteRevokeInframailIpsRequestError,
          onError: () => {
            toaster.error(deleteRevokeInframailIpsRequestError.message);
          },
        });
      },
    });
  }, [deleteRevokeInframailIpsRequestStatus]);

  const isLoading = getIsRequestPending(getInframailIpsRequestStatus);

  return (
    <div>
      <InframailIpsTable
        inframailIps={inframailIps}
        inframailIpsMeta={inframailIpsMeta}
        onPaginationChange={onPaginationChange}
        isLoading={isLoading}
        onAction={onAction}
        onAddMoreDomainsClicked={onAddMoreDomainsClicked}
        onAddMoreEmailAccountsClicked={onAddMoreEmailAccountsClicked}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={onCloseDeleteModal}
        isLoading={getIsRequestPending(deleteRevokeInframailIpsRequestStatus)}
        onSubmit={() => {
          sendDeleteRevokeInframailIpsRequest({
            id: actionRow.orderId,
            action: InframailIpAction.DELETE,
          });
        }}
      />

      <RevokeModal
        show={showRevokeModal}
        onClose={onCloseRevokeModal}
        isLoading={getIsRequestPending(deleteRevokeInframailIpsRequestStatus)}
        onSubmit={() => {
          sendDeleteRevokeInframailIpsRequest({
            id: actionRow.orderId,
            action: InframailIpAction.REVOKE,
          });
        }}
      />
    </div>
  );
};

export default InframailIpsContent;
